import React, { useState, useEffect } from 'react';
import { MessageCircle, UserRound } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { useConversations } from '../../hooks/whatsapp/useChat';
import { useChatStore } from '../../stores/whatsapp/chatStore';
import ConversationList from './ConversationList';
import ContactList from './ContactList';
import ChatWindow from './ChatWindow';
import ClientInfoPanel from './ClientInfoPanel';
import OpenTicketModal from './OpenTicketModal';
import { useAuth } from '../../providers/auth';
import { useDeptVisibilityStore } from '../../stores/whatsapp/deptVisibilityStore';
import { useKanbanStore } from '../../stores/whatsapp/kanbanStore';
import waApi from '../../services/whatsapp/waApi';

type LeftTab = 'conversations' | 'contacts';

interface TicketContact {
  id: string;
  name: string;
  phone: string;
  company?: string;
}

export default function ChatPage() {
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [showClientPanel, setShowClientPanel] = useState(false);
  const [leftTab, setLeftTab] = useState<LeftTab>('conversations');
  const [mobileView, setMobileView] = useState<'list' | 'chat'>('list');
  const [ticketContact, setTicketContact] = useState<TicketContact | null>(null);
  const [showTicketModal, setShowTicketModal] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const { data: conversations, refresh: refreshConversations } = useConversations();
  const clearUnread = useChatStore(s => s.clearUnread);
  const setStoreActiveConversation = useChatStore(s => s.setActiveConversation);
  const wsReconnecting = useChatStore(s => s.wsReconnecting);
  // O WebSocket do chat e os listeners de notificacao agora vivem no
  // WhatsAppNotificationProvider (global, em App.tsx), para que o sino receba
  // mensagens em qualquer modulo. Nao montar aqui evita conexao duplicada.

  // Carregar tickets do kanban na inicializacao
  const kanbanLoaded = useKanbanStore(s => s.loaded);
  const fetchKanbanTickets = useKanbanStore(s => s.fetchTickets);
  useEffect(() => {
    if (!kanbanLoaded) fetchKanbanTickets();
  }, [kanbanLoaded, fetchKanbanTickets]);

  // Inicializar visibilidade por departamento
  const { user, isAdmin } = useAuth();
  const setDeptConfig = useDeptVisibilityStore(s => s.setConfig);

  useEffect(() => {
    if (!user) return;
    const currentUser = user;

    // Admin ve tudo — sem restricao de departamento
    if (isAdmin()) {
      setDeptConfig({
        restrictByDept: false,
        userDept: '',
        blurMessages: false,
        allowedExtraDepts: [],
      });
      return;
    }

    // Departamento do usuario vem do campo departamento (vinculado na tela de usuarios)
    const userDept = currentUser?.departamento?.nome || '';
    const grupoId = currentUser?.grupo_id;

    // Busca config de departamentos da API, fallback localStorage
    const loadDeptConfig = async () => {
      let config: { restrict_by_dept?: boolean; blur_messages?: boolean; extra_depts?: string[] } =
        {};

      // Tenta API primeiro
      if (grupoId) {
        try {
          const res = await waApi.get(`/dept-config/${grupoId}`);
          const data = res.data?.data ?? res.data;
          if (data) config = data;
        } catch {
          // API nao disponivel — tenta localStorage
          try {
            const saved = localStorage.getItem(`wa_dept_config_grupo_${grupoId}`);
            if (saved) {
              const parsed = JSON.parse(saved);
              config = {
                restrict_by_dept: parsed.restrictByDept,
                blur_messages: parsed.blurMessages,
                extra_depts: parsed.extraDepts,
              };
            }
          } catch {
            /* ignore */
          }
        }
      }

      const restrictByDept = config.restrict_by_dept ?? !!userDept;
      const blurMessages = config.blur_messages ?? true;
      const extraDepts = config.extra_depts ?? [];

      if (restrictByDept && userDept) {
        setDeptConfig({
          restrictByDept: true,
          userDept,
          allowedExtraDepts: extraDepts,
          blurMessages,
        });
      } else {
        setDeptConfig({
          restrictByDept: false,
          userDept: '',
          allowedExtraDepts: [],
          blurMessages: false,
        });
      }
    };

    loadDeptConfig();
  }, [user, isAdmin, setDeptConfig]);

  // Limpar a conversa ativa do store ao sair da tela de chat. O
  // useChatNotifications (global) suprime a notificacao do sino para a
  // conversa ativa — sem este cleanup, a ultima conversa aberta ficava
  // "ativa" para sempre e suas mensagens nunca apareciam no sino.
  useEffect(
    () => () => {
      setStoreActiveConversation(null);
    },
    [setStoreActiveConversation],
  );

  // Open conversation from URL query param (e.g. ?conversation=abc)
  useEffect(() => {
    const convId = searchParams.get('conversation');
    if (convId) {
      setActiveConversationId(convId);
      setStoreActiveConversation(convId);
      setMobileView('chat');
      setLeftTab('conversations');
      searchParams.delete('conversation');
      setSearchParams(searchParams, { replace: true });

      // Mark as read (same as handleSelectConversation)
      clearUnread(convId);
      waApi.post(`/conversations/${convId}/read`).catch(() => {});
    }
  }, [searchParams, setSearchParams]);

  const activeConversation = conversations.find(c => c.id === activeConversationId) || null;

  const handleSelectConversation = (id: string) => {
    setActiveConversationId(id);
    setStoreActiveConversation(id);
    setShowClientPanel(false);
    setLeftTab('conversations');
    setMobileView('chat');

    // Mark as read on backend + clear local unread
    clearUnread(id);
    waApi.post(`/conversations/${id}/read`).catch(() => {});
  };

  const handleToggleClientPanel = () => {
    setShowClientPanel(prev => !prev);
  };

  const handleStartConversation = (contact: {
    id: string;
    name: string;
    phone: string;
    company?: string;
  }) => {
    const existing = conversations.find(
      c => c.client_phone === contact.phone && c.status !== 'closed',
    );
    if (existing) {
      setActiveConversationId(existing.id);
      setStoreActiveConversation(existing.id);
      setLeftTab('conversations');
      setMobileView('chat');
      toast('Conversa existente encontrada', { icon: '💬' });
    } else {
      // Sem chamado aberto → abrir modal para criar
      setTicketContact(contact);
      setShowTicketModal(true);
    }
  };

  const handleTicketCreated = (conversationId: string) => {
    refreshConversations();
    setActiveConversationId(conversationId);
    setStoreActiveConversation(conversationId);
    setLeftTab('conversations');
    setMobileView('chat');
  };

  const handleBackToList = () => {
    setMobileView('list');
  };

  return (
    <div className="flex h-full flex-col bg-gray-50">
      {/* Banner de reconexão do WebSocket (issue do gap de reconexão) */}
      {wsReconnecting && (
        <div className="flex flex-shrink-0 items-center justify-center gap-2 bg-amber-50 px-4 py-1.5 text-xs font-medium text-amber-700">
          <span className="h-2 w-2 animate-pulse rounded-full bg-amber-500" />
          Reconectando ao tempo real… as mensagens serão atualizadas automaticamente.
        </div>
      )}
      <div className="flex min-h-0 flex-1 bg-gray-50">
        {/* Left panel */}
        <div
          className={clsx(
            'flex w-full flex-shrink-0 flex-col border-r border-gray-200 bg-white md:w-80 lg:w-96',
            mobileView === 'chat' ? 'hidden md:flex' : 'flex',
          )}
        >
          {/* Tab switcher */}
          <div className="flex items-center gap-2 px-4 pb-2 pt-3">
            <button
              type="button"
              onClick={() => setLeftTab('conversations')}
              className={clsx(
                'flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all',
                leftTab === 'conversations'
                  ? 'bg-green-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200',
              )}
            >
              <MessageCircle size={15} />
              Conversas
            </button>
            <button
              type="button"
              onClick={() => setLeftTab('contacts')}
              className={clsx(
                'flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all',
                leftTab === 'contacts'
                  ? 'bg-green-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200',
              )}
            >
              <UserRound size={15} />
              Contatos
            </button>
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-hidden">
            {leftTab === 'conversations' ? (
              <ConversationList
                conversations={conversations}
                activeId={activeConversationId}
                onSelect={handleSelectConversation}
              />
            ) : (
              <ContactList onStartConversation={handleStartConversation} />
            )}
          </div>
        </div>

        {/* Chat window or empty state */}
        <div
          className={clsx(
            'flex min-w-0 flex-1 flex-col',
            mobileView === 'list' ? 'hidden md:flex' : 'flex',
          )}
        >
          {activeConversation ? (
            <ChatWindow
              conversationId={activeConversation.id}
              conversation={activeConversation}
              onToggleClientPanel={handleToggleClientPanel}
              onBack={handleBackToList}
            />
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center bg-gray-50 text-gray-400">
              <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-green-50">
                <MessageCircle className="h-10 w-10 text-green-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-600">Central de Relacionamento</h3>
              <p className="mt-1.5 max-w-xs text-center text-sm text-gray-400">
                Selecione uma conversa ou contato para começar
              </p>
            </div>
          )}
        </div>

        {/* Client info panel */}
        {showClientPanel && activeConversation && (
          <div
            className={clsx(
              'flex-shrink-0 overflow-y-auto border-l border-gray-200 bg-white',
              'fixed inset-y-0 right-0 z-50 w-80 md:static md:z-auto lg:w-80',
            )}
          >
            <ClientInfoPanel
              conversation={activeConversation}
              onClose={() => setShowClientPanel(false)}
            />
          </div>
        )}

        {showClientPanel && activeConversation && (
          <div
            className="fixed inset-0 z-40 bg-black/20 md:hidden"
            role="button"
            tabIndex={0}
            aria-label="Fechar painel"
            onClick={() => setShowClientPanel(false)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') setShowClientPanel(false);
            }}
          />
        )}

        {/* Open Ticket Modal */}
        <OpenTicketModal
          isOpen={showTicketModal}
          onClose={() => setShowTicketModal(false)}
          contact={ticketContact}
          onTicketCreated={handleTicketCreated}
        />
      </div>
    </div>
  );
}
